import React, { useState, useEffect, useCallback } from 'react'
import { Profile, GuideProfile, isDemoMode } from '../lib/supabase'
import { ProfileService, GuideProfileService } from '../services/profileService'
import { ProfileForm } from './ProfileForm'
import { PublicProfileView } from './PublicProfileView'
import { Button } from './ui/button'
import { motion } from 'framer-motion'
import { Edit, Settings, UserPlus } from 'lucide-react'

interface UserProfileDashboardProps {
  userId?: string // If provided, shows public view for that user
}

export const UserProfileDashboard: React.FC<UserProfileDashboardProps> = ({ userId }) => {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [guideProfile, setGuideProfile] = useState<GuideProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isOwner, setIsOwner] = useState(false)

  const loadProfileData = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      let profileData: Profile | null = null
      let guideData: GuideProfile | null = null
      
      if (userId) {
        // Loading public profile for specific user
        profileData = await ProfileService.getPublicProfile(userId)
        guideData = await GuideProfileService.getGuideProfile(userId)
        setIsOwner(false)
      } else {
        // Loading current user's profile
        profileData = await ProfileService.getMyProfile()
        if (profileData) {
          guideData = await GuideProfileService.getGuideProfile(profileData.user_id)
        }
        setIsOwner(true)
      }
      
      setProfile(profileData)
      setGuideProfile(guideData)
      
    } catch (error) {
      console.error('Error loading profile data:', error)
      const errorMessage = isDemoMode 
        ? 'Demo Mode: Profile data is not available. Please configure Supabase credentials to enable profile management.'
        : 'Failed to load profile data. Please try again.'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [userId])

  useEffect(() => {
    loadProfileData()
  }, [loadProfileData])

  const handleProfileUpdate = (updatedProfile: Profile) => {
    setProfile(updatedProfile)
    setIsEditing(false)
  }

  const handleBecomeGuide = () => {
    // This would typically open a guide registration form
    alert('Guide registration feature coming soon!')
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">{error}</div>
          <Button onClick={loadProfileData}>
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500 text-xl mb-4">
            {userId ? 'Profile not found' : 'No profile data available'}
          </div>
          {!userId && (
            <Button onClick={() => setIsEditing(true)}>
              Create Profile
            </Button>
          )}
        </div>
      </div>
    )
  }

  // If editing mode, show the form
  if (isEditing && isOwner) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <ProfileForm
          profile={profile}
          onProfileUpdate={handleProfileUpdate}
          onClose={() => setIsEditing(false)}
        />
      </div>
    )
  }

  // Show public profile view
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <PublicProfileView 
        profile={profile} 
        guideProfile={guideProfile}
        isLoading={isLoading}
      />

      {/* Owner Actions */}
      {isOwner && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="max-w-4xl mx-auto px-6 mt-6"
        >
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Profile Actions</h3>
            
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => setIsEditing(true)}
                className="flex items-center space-x-2"
              >
                <Edit className="h-4 w-4" />
                <span>Edit Profile</span>
              </Button>

              {!guideProfile && (
                <Button
                  onClick={handleBecomeGuide}
                  variant="outline"
                  className="flex items-center space-x-2"
                >
                  <UserPlus className="h-4 w-4" />
                  <span>Become a Guide</span>
                </Button>
              )}

              <Button
                variant="outline"
                className="flex items-center space-x-2"
              >
                <Settings className="h-4 w-4" />
                <span>Settings</span>
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  )
}

export default UserProfileDashboard